import React, { useEffect, useState } from "react"
import FeesAndTaxesSettings, {
  OptionKeys,
  ToggleFeesTypes,
} from "../../Reservations/NewReservationModal/FeesAndTaxesSettings"
import Spinner from "~/components/ui/Spinner"
import IOrder from "~/interfaces/IOrder"
import IOrderPreview from "~/interfaces/IOrderPreview"
import { CampingStyleProps } from "~/interfaces"
import ePaymentType, { PaymentType } from "~/enums/ePaymentType"

interface SetTaxFeesProps {
  order: IOrder | IOrderPreview
  campingStyle: CampingStyleProps | undefined
  onUpdateFees?: (fees: Partial<Record<keyof ToggleFeesTypes, boolean>>) => void
  isStore?: boolean
  defaultTaxFeeEnabled?: boolean
  defaultCreditCardFeeEnabled?: boolean
  isLongTermElectric?: boolean
  paymentMethod?: PaymentType
  isInvoice?: boolean
}

interface SetTaxFeesReturn {
  taxFeeEnabled: boolean
  creditCardFeeEnabled: boolean
  setTaxFeeEnabled: React.Dispatch<React.SetStateAction<boolean>>
  setCreditCardFeeEnabled: React.Dispatch<React.SetStateAction<boolean>>
  TaxToggle: React.ReactElement
}

const useSetTaxFees = ({
  order,
  campingStyle,
  onUpdateFees,
  isStore,
  defaultTaxFeeEnabled,
  defaultCreditCardFeeEnabled,
  isLongTermElectric,
  paymentMethod,
  isInvoice
}: SetTaxFeesProps): SetTaxFeesReturn => {
  const [creditCardFeeEnabled, setCreditCardFeeEnabled] = React.useState<boolean>(!!defaultCreditCardFeeEnabled)
  const [taxFeeEnabled, setTaxFeeEnabled] = useState<boolean>(!!defaultTaxFeeEnabled || (isLongTermElectric ?? false))
  const [enabledFees, setEnabledFees] = useState<Partial<Record<keyof ToggleFeesTypes, boolean>>>()
  const isPartialPayment = !!(order?.paid_total_in_cents && order?.paid_total_in_cents > 0)

  useEffect(() => {
    setTaxFeeEnabled(
      !!defaultTaxFeeEnabled || order?.line_items?.some((item) => item.tax_rate > 0) || (isLongTermElectric ?? false),
    )
    setCreditCardFeeEnabled(!!defaultCreditCardFeeEnabled || order?.credit_card_rate > 0)
  }, [])

  useEffect(() => {
    setEnabledFees((prevState) => ({
      ...prevState,
      ["is_credit_card_fee_enabled"]: creditCardFeeEnabled,
      ["is_tax_enabled"]: taxFeeEnabled,
    }))

    if (onUpdateFees) {
      onUpdateFees({
        is_credit_card_fee_enabled: creditCardFeeEnabled,
        is_tax_enabled: taxFeeEnabled,
      })
    }
  }, [creditCardFeeEnabled, taxFeeEnabled])

  const switchEnabledFees = (key: keyof ToggleFeesTypes, value: boolean) => {
    const newEnabledFees = { ...enabledFees }
    newEnabledFees[key] = value
    setEnabledFees(newEnabledFees)

    if (key === "is_credit_card_fee_enabled") {
      setCreditCardFeeEnabled(value)
    } else if (key === "is_tax_enabled") {
      setTaxFeeEnabled(value)
    }
  }

  const dropdownOptions: Array<OptionKeys> = ["taxRate", "creditCard"]

  useEffect(() => {
    if (
      paymentMethod &&
      [ePaymentType.CARD_READER, ePaymentType.CARD_INPUT, ePaymentType.LINK].includes(paymentMethod)
    ) {
      setCreditCardFeeEnabled(true)
    } else {
      setCreditCardFeeEnabled(false)
    }
  }, [paymentMethod])

  return {
    taxFeeEnabled,
    creditCardFeeEnabled,
    setTaxFeeEnabled,
    setCreditCardFeeEnabled,
    TaxToggle: !order ? (
      <Spinner size={10} />
    ) : (
      <FeesAndTaxesSettings
        enabledFees={enabledFees}
        options={dropdownOptions}
        setValue={(key, value) => switchEnabledFees(key, value)}
        campingStyle={campingStyle}
        isGroup={false}
        isStore={isStore}
        isLongTermElectric={isLongTermElectric}
        isPartiallyPaid={isPartialPayment}
        paymentMethod={paymentMethod}
        isInvoice={isInvoice}
      />
    ),
  }
}

export default useSetTaxFees
